import type { TFunction } from 'i18next';

import type {
  EnrichedProductField,
  FailedProduct,
  ProductScore,
  RunEnrichedProduct,
} from '../types/run-content.api.types';
import type { PendingGate } from '../types/run.api.types';

import { enrichedFieldKind, fieldKey, fieldValueText, isGenerated } from './enriched-fields';
import { SCORE_BAND } from './run-score-fields';
import { parseSourceProductId } from './source-product-id';

/**
 * Client-side review model for the Enrichment gate (Levels 1–3). Everything here is computed in
 * the browser over a CAPPED sample of the run's enriched products, because content-generation has
 * no aggregation / reason / risk endpoints yet. The reason checks are HEURISTICS — documented per
 * rule below and in the review help panel — never a verdict the server made.
 */

/** Why a product needs a look. Order = display order (most severe first). */
export type ReviewReason =
  | 'generationFailed'
  | 'scoreDropped'
  | 'scorerFlagged'
  | 'brandDropped'
  | 'numbersRemoved'
  | 'claimAdded'
  | 'variantInconsistent'
  | 'titleTooLong'
  | 'fieldNotGenerated'
  | 'descriptionTooShort'
  | 'descriptionTooLong';

export const REVIEW_REASONS: readonly ReviewReason[] = [
  'generationFailed',
  'scoreDropped',
  'scorerFlagged',
  'brandDropped',
  'numbersRemoved',
  'claimAdded',
  'variantInconsistent',
  'titleTooLong',
  'fieldNotGenerated',
  'descriptionTooShort',
  'descriptionTooLong',
];

/** True for a known review reason — narrows a URL value without a cast. */
export function isReviewReason(value: unknown): value is ReviewReason {
  return typeof value === 'string' && REVIEW_REASONS.some((r) => r === value);
}

/** Design B — reasons that mean something was lost or invented (red dot); the rest are amber. */
const LOST_OR_INVENTED: ReadonlySet<ReviewReason> = new Set([
  'generationFailed',
  'numbersRemoved',
  'claimAdded',
]);

/** True when a reason marks real loss/invention — the red dot on WHERE TO LOOK and the lens. */
export function isLostOrInvented(reason: ReviewReason): boolean {
  return LOST_OR_INVENTED.has(reason);
}

/** Reasons drawn in error tone (G1); every other reason is a warning. */
export const ERROR_REASONS: ReadonlySet<ReviewReason> = new Set(['generationFailed']);

/**
 * Thresholds behind the heuristics. Title 150 and description 5,000 are Google Merchant Center's
 * hard limits; the 160-character description floor is a review heuristic (a description shorter
 * than a search snippet rarely carries enough), not a channel rule.
 */
export const REVIEW_LIMITS = { titleMax: 150, descriptionMin: 160, descriptionMax: 5000 } as const;

/** Risk weight per reason — the queue's default "Risk" sort adds these up (heuristic). */
const REASON_WEIGHT: Record<ReviewReason, number> = {
  generationFailed: 100,
  scoreDropped: 40,
  scorerFlagged: 35,
  brandDropped: 30,
  numbersRemoved: 25,
  claimAdded: 25,
  variantInconsistent: 22,
  titleTooLong: 20,
  fieldNotGenerated: 15,
  descriptionTooShort: 10,
  descriptionTooLong: 10,
};

/** One heuristic hit on one product, optionally pinned to the field it concerns. */
export interface ReviewFinding {
  reason: ReviewReason;
  /** Normalised field key (`title`, `description`, …) or null for product-level findings. */
  field: string | null;
  /** Short evidence for the row / detail ("SPF 50, 150 ml", "162 characters", "94 → 88"). */
  detail: string | null;
}

/** Rough size of what enrichment rewrote on a product, in changed words (word-set estimate). */
export type ChangeSize = 'none' | 'small' | 'medium' | 'large';

/** One field's score before / after with the scorer's note — for the row and the focus detail. */
export interface FieldScore {
  pre: number | null;
  post: number | null;
  issue: string | null;
}

/** The worst field on a product: the biggest score drop, else the lowest post score, else a finding. */
export interface WorstField {
  field: string;
  issue: string | null;
  pre: number | null;
  post: number | null;
}

/** One product in the review queue (enriched or failed), with everything the three levels read. */
export interface ReviewItem {
  productId: string;
  externalId: string | null;
  title: string;
  brand: string | null;
  /** Market code parsed from the connector id (`UK`, `US`) when it encodes one. */
  market: string | null;
  /** The matched product's combined kind (`UNIQUE` / `VARIANT` / `SET` / `COMPONENT`, whatever the
   *  curation join's `product.product_level` sent — "In catalog as" elsewhere in this app); null
   *  when unmatched or the join has not loaded this product. Drives the Type filter. */
  productLevel: string | null;
  /** The matched product's style-family key (`product.style_anchor_id` on the curation row) — the
   *  id a VARIANT and its siblings (and their shared UNIQUE) all carry; null for a lone product, a
   *  SET/COMPONENT, or an unmatched row. Drives "Group: Family". */
  familyKey: string | null;
  /** Full-size image (focus mode, viewer). */
  imageUrl: string | null;
  /** Small-thumb image for queue rows (160px thumbnail when recorded, else `imageUrl`). */
  thumbUrl: string | null;
  gallery: string[];
  /** Listed by content-gen's failed-products read (or an enriched row whose status failed). */
  failed: boolean;
  /** A failure RETRY_FAILED_ITEMS would re-run. */
  retryable: boolean;
  status: string;
  errorMessage: string | null;
  pre: number | null;
  post: number | null;
  /** post − pre, null unless both exist. */
  delta: number | null;
  /** The SERVER's own PASS/REVIEW/FAIL tier (post phase's, else pre's) — never recomputed here;
   *  null when the product has no score row (e.g. it failed before scoring). */
  tier: string | null;
  /** The requested fields on this product (never the connector's whole vocabulary). */
  fields: EnrichedProductField[];
  fieldScores: Record<string, FieldScore>;
  findings: ReviewFinding[];
  /** Distinct reasons, in display order. */
  reasons: ReviewReason[];
  changedWords: number;
  changeSize: ChangeSize;
  worst: WorstField | null;
  risk: number;
  /** The enriched row as content-gen sent it (null for a failed-only product). */
  row: RunEnrichedProduct | null;
}

/** Names / thumbs / brand joined from the run's curation products, keyed by catalog product id. */
export interface ReviewJoin {
  title: string | null;
  brand: string | null;
  imageUrl: string | null;
  thumbUrl: string | null;
  gallery: string[];
  /** See `ReviewItem.productLevel` — read off the same curation row this join already walks. */
  productLevel: string | null;
  /** See `ReviewItem.familyKey`. */
  familyKey: string | null;
}

/** One enriched row plus its PRE / POST score rows (null when that phase has none). */
export interface EnrichedSampleRow {
  row: RunEnrichedProduct;
  pre: ProductScore | null;
  post: ProductScore | null;
}

// ---------------------------------------------------------------------------------------------
// Requested fields
// ---------------------------------------------------------------------------------------------

/** The fields the run was STARTED with (`config.request.useCase.fields`), in the order chosen. */
export function chosenFieldNames(request: unknown): string[] {
  if (!request || typeof request !== 'object') return [];
  const useCase = (request as { useCase?: { fields?: unknown } }).useCase;
  return Array.isArray(useCase?.fields)
    ? useCase.fields.filter((f): f is string => typeof f === 'string')
    : [];
}

/**
 * The requested field keys: chosen ∪ always-on. Only when the run recorded no choice do the rows'
 * own field names stand in — never the connector's field vocabulary (that showed "5 of 58").
 */
export function requestedFieldKeys(
  chosen: readonly string[],
  alwaysOn: readonly string[],
  fallbackNames: readonly string[],
): Set<string> {
  const names = chosen.length > 0 ? [...chosen, ...alwaysOn] : fallbackNames;
  return new Set(names.map(fieldKey));
}

// ---------------------------------------------------------------------------------------------
// Text heuristics
// ---------------------------------------------------------------------------------------------

/** The value that ships for a field: an operator's effective value, else the generated one. */
export function afterText(
  field: EnrichedProductField,
  t?: TFunction<'catalog-v2-run'>,
): string | null {
  return fieldValueText(field.effective, t) ?? fieldValueText(field.generated, t);
}

const WORD_RE = /[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu;

/** Lower-cased words of a text (letters / digits, in-word apostrophes and hyphens kept). */
function words(text: string | null): string[] {
  return text ? (text.toLowerCase().match(WORD_RE) ?? []) : [];
}

/** Words removed and added between two texts, as multisets (order-free, O(n)). */
export function wordBagDiff(
  before: string | null,
  after: string | null,
): { removed: string[]; added: string[] } {
  const counts = new Map<string, number>();
  for (const w of words(before)) counts.set(w, (counts.get(w) ?? 0) + 1);
  const added: string[] = [];
  for (const w of words(after)) {
    const n = counts.get(w) ?? 0;
    if (n > 0) counts.set(w, n - 1);
    else added.push(w);
  }
  const removed: string[] = [];
  for (const [w, n] of counts) for (let i = 0; i < n; i++) removed.push(w);
  return { removed, added };
}

/** Units a number can carry in a product text (sizes, volumes, weights, counts, durations, %). */
const UNIT =
  'fl\\.?\\s?oz|ml|cl|l|oz|mg|kg|g|lbs?|cm|mm|m|in|inch(?:es)?|%|pcs|pack|ct|count|hrs?|hours?|h|x';
const NUMBER_UNIT_RE = new RegExp(`(\\d+(?:[.,]\\d+)?)\\s*(${UNIT})(?![\\p{L}])`, 'giu');
const SPF_RE = /\bspf\s*(\d+)\s*\+?/giu;

/**
 * Number + unit facts in a text, normalised ("SPF 50" → "spf50", "150 ml" → "150ml",
 * "1,7 oz" → "1.7oz"). Heuristic: a fact in the feed's value that is missing from the new
 * value may mean enrichment dropped a size, strength or claim the shopper relies on.
 */
export function numberUnitFacts(text: string | null): Map<string, string> {
  const facts = new Map<string, string>();
  if (!text) return facts;
  for (const m of text.matchAll(SPF_RE)) facts.set(`spf${m[1] ?? ''}`, m[0].trim());
  for (const m of text.matchAll(NUMBER_UNIT_RE)) {
    const unit = (m[2] ?? '').toLowerCase().replace(/[.\s]/g, '');
    const key = `${(m[1] ?? '').replace(',', '.')}${unit}`;
    if (!facts.has(key)) facts.set(key, m[0].trim());
  }
  return facts;
}

/** Facts of `before` that `after` no longer states (original spelling, for display). */
export function removedFacts(before: string | null, after: string | null): string[] {
  const kept = numberUnitFacts(after);
  return [...numberUnitFacts(before)].filter(([key]) => !kept.has(key)).map(([, raw]) => raw);
}

/**
 * Marketing / regulatory claims worth a human look when enrichment ADDS them: a claim the feed
 * never made may be invented. Heuristic list, matched on whole words.
 */
const CLAIMS = [
  'organic',
  'vegan',
  'cruelty-free',
  'paraben-free',
  'sulfate-free',
  'fragrance-free',
  'gluten-free',
  'hypoallergenic',
  'non-comedogenic',
  'dermatologist tested',
  'dermatologist recommended',
  'clinically proven',
  'clinically tested',
  'waterproof',
  'natural',
  'eco-friendly',
  'sustainable',
  'recyclable',
  'award-winning',
  'best-selling',
  'medical grade',
  'fda approved',
  'reef safe',
];

/** Claims present in `after` that `before` never made. */
export function addedClaims(before: string | null, after: string | null): string[] {
  const has = (text: string | null, claim: string) =>
    !!text &&
    new RegExp(`(^|[^\\p{L}])${claim.replace(/[-\s]/g, '[-\\s]')}([^\\p{L}]|$)`, 'iu').test(text);
  return CLAIMS.filter((c) => has(after, c) && !has(before, c));
}

/** Escapes a literal for use inside a RegExp. */
function escapeRe(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** True when `text` names the brand as a whole word (case-insensitive, whitespace-tolerant). */
function mentions(text: string | null, brand: string): boolean {
  const words = brand.trim().split(/\s+/).map(escapeRe).join('\\s+');
  return (
    !!text &&
    !!words &&
    new RegExp(`(^|[^\\p{L}\\p{N}])${words}([^\\p{L}\\p{N}]|$)`, 'iu').test(text)
  );
}

// ---------------------------------------------------------------------------------------------
// Building review items
// ---------------------------------------------------------------------------------------------

/** Changed-word count → the queue's Change size bucket. */
export function changeSizeOf(changedWords: number): ChangeSize {
  if (changedWords === 0) return 'none';
  if (changedWords <= 5) return 'small';
  if (changedWords <= 20) return 'medium';
  return 'large';
}

/** A score value off one phase's score row, or null. */
function scoreOf(score: ProductScore | null, name: string): number | null {
  const v = score?.scores[name];
  return typeof v === 'number' ? v : null;
}

/** The brand: the curation join's brand, else the requested `brand` field's feed value. */
function brandOf(join: ReviewJoin | undefined, fields: EnrichedProductField[]): string | null {
  if (join?.brand) return join.brand;
  const field = fields.find((f) => fieldKey(f.name) === 'brand');
  return field ? (fieldValueText(field.before) ?? afterText(field)) : null;
}

/** All heuristic findings on one field of one product. */
function fieldFindings(field: EnrichedProductField, brand: string | null): ReviewFinding[] {
  const key = fieldKey(field.name);
  const kind = enrichedFieldKind(field);
  const out: ReviewFinding[] = [];
  if (kind === 'failed') return [{ reason: 'generationFailed', field: key, detail: field.state }];
  if (kind === 'notGenerated' || kind === 'rejected') {
    return [{ reason: 'fieldNotGenerated', field: key, detail: null }];
  }
  if (!isGenerated(field) && kind !== 'edited') return out;
  const before = fieldValueText(field.before);
  const after = afterText(field);
  if (key === 'title' && after !== null && after.length > REVIEW_LIMITS.titleMax) {
    out.push({ reason: 'titleTooLong', field: key, detail: String(after.length) });
  }
  if (key === 'title' && brand && mentions(before, brand) && !mentions(after, brand)) {
    out.push({ reason: 'brandDropped', field: key, detail: brand });
  }
  if (key === 'description' && after !== null) {
    if (after.length < REVIEW_LIMITS.descriptionMin) {
      out.push({ reason: 'descriptionTooShort', field: key, detail: String(after.length) });
    } else if (after.length > REVIEW_LIMITS.descriptionMax) {
      out.push({ reason: 'descriptionTooLong', field: key, detail: String(after.length) });
    }
  }
  const lost = removedFacts(before, after);
  if (lost.length > 0) out.push({ reason: 'numbersRemoved', field: key, detail: lost.join(', ') });
  const claims = addedClaims(before, after);
  if (claims.length > 0) out.push({ reason: 'claimAdded', field: key, detail: claims.join(', ') });
  return out;
}

/**
 * Raw scorer messages this screen can restate in plain language, keyed by the i18n suffix
 * `enrichment.review.scorerIssue.<key>` that carries the rewrite. Anything else falls back to a
 * trimmed, quoted "Scorer: “…”" line (`enrichment.review.scorerIssue.generic`) — the content-gen
 * scorer's wording is free text and must never be shown as stray quotes/symbols on its own.
 */
const KNOWN_SCORER_ISSUES: ReadonlyMap<string, string> = new Map([
  ["' > ' levels is missing", 'categoryLevelsMissing'],
]);

/** The i18n key for a known scorer message (trimmed, exact match), else null for the generic fallback. */
export function knownScorerIssueKey(issue: string | null): string | null {
  const trimmed = issue?.trim();
  return trimmed ? (KNOWN_SCORER_ISSUES.get(trimmed) ?? null) : null;
}

/** Per-field PRE / POST score and the scorer's note, for the requested fields that have one. */
function fieldScoresOf(
  keys: readonly string[],
  pre: ProductScore | null,
  post: ProductScore | null,
): Record<string, FieldScore> {
  const out: Record<string, FieldScore> = {};
  for (const key of keys) {
    const p = scoreOf(pre, key);
    const q = scoreOf(post, key);
    if (p === null && q === null) continue;
    out[key] = {
      pre: p,
      post: q,
      issue: post?.feedback?.[key]?.issue ?? pre?.feedback?.[key]?.issue ?? null,
    };
  }
  return out;
}

/** Biggest field-score drop, else the lowest post score below "good", else the first finding. */
function worstFieldOf(
  scores: Record<string, FieldScore>,
  findings: ReviewFinding[],
): WorstField | null {
  let worst: WorstField | null = null;
  let worstDrop = 0;
  for (const [field, s] of Object.entries(scores)) {
    if (s.pre !== null && s.post !== null && s.post - s.pre < worstDrop) {
      worstDrop = s.post - s.pre;
      worst = { field, ...s };
    }
  }
  if (worst) return worst;
  let lowest: WorstField | null = null;
  for (const [field, s] of Object.entries(scores)) {
    const v = s.post ?? s.pre;
    if (
      v !== null &&
      v < SCORE_BAND.good &&
      (lowest === null || v < (lowest.post ?? lowest.pre ?? 101))
    ) {
      lowest = { field, ...s };
    }
  }
  if (lowest) return lowest;
  const pinned = findings.find((f) => f.field !== null);
  return pinned?.field
    ? { field: pinned.field, issue: null, pre: null, post: null, ...(scores[pinned.field] ?? {}) }
    : null;
}

/** Risk = reason weights + the score drop + how far post sits under "fair" (heuristic sort key). */
function riskOf(reasons: ReviewReason[], pre: number | null, post: number | null): number {
  let risk = reasons.reduce((sum, r) => sum + REASON_WEIGHT[r], 0);
  if (pre !== null && post !== null && post < pre) risk += pre - post;
  const v = post ?? pre;
  if (v !== null && v < SCORE_BAND.fair) risk += (SCORE_BAND.fair - v) / 2;
  return risk;
}

/** Distinct reasons of a finding list, in display order. */
function reasonsOf(findings: ReviewFinding[]): ReviewReason[] {
  const set = new Set(findings.map((f) => f.reason));
  return REVIEW_REASONS.filter((r) => set.has(r));
}

/** Fallback title: the requested title field's feed value, else the parsed offer id. */
function fallbackTitle(fields: EnrichedProductField[], externalId: string | null, id: string) {
  const title = fields.find((f) => fieldKey(f.name) === 'title');
  return (
    (title ? fieldValueText(title.before) : null) ??
    parseSourceProductId(externalId)?.offerId ??
    externalId ??
    id
  );
}

/** One enriched row → review item: requested fields only, heuristics, worst field, risk. */
function enrichedItem(
  sample: EnrichedSampleRow,
  join: ReviewJoin | undefined,
  requested: ReadonlySet<string>,
): ReviewItem {
  const { row } = sample;
  const fields =
    requested.size > 0 ? row.fields.filter((f) => requested.has(fieldKey(f.name))) : row.fields;
  const brand = brandOf(join, row.fields);
  const pre = scoreOf(sample.pre, 'overall');
  const post = scoreOf(sample.post, 'overall');
  const fieldScores = fieldScoresOf(
    [...new Set(fields.map((f) => fieldKey(f.name)))],
    sample.pre,
    sample.post,
  );
  const findings = fields.flatMap((f) => fieldFindings(f, brand));
  // The scorer's own feedback is a status, not just row colour — a field below "good" with a
  // scorer note needs a look even when every client heuristic above passed it clean.
  for (const [key, score] of Object.entries(fieldScores)) {
    const value = score.post ?? score.pre;
    if (value !== null && value < SCORE_BAND.good && score.issue?.trim()) {
      findings.push({ reason: 'scorerFlagged', field: key, detail: score.issue });
    }
  }
  const failed = row.status.toUpperCase().includes('FAIL');
  if (failed && !findings.some((f) => f.reason === 'generationFailed')) {
    findings.unshift({ reason: 'generationFailed', field: null, detail: row.errorMessage });
  }
  if (pre !== null && post !== null && post < pre) {
    findings.unshift({
      reason: 'scoreDropped',
      field: null,
      detail: `${pre.toFixed(0)} → ${post.toFixed(0)}`,
    });
  }
  const changedWords = fields.reduce((sum, f) => {
    if (!isGenerated(f)) return sum;
    const { removed, added } = wordBagDiff(fieldValueText(f.before), afterText(f));
    return sum + Math.max(removed.length, added.length);
  }, 0);
  const reasons = reasonsOf(findings);
  return {
    productId: row.productId,
    externalId: row.externalId,
    title: join?.title ?? fallbackTitle(row.fields, row.externalId, row.productId),
    brand,
    market: parseSourceProductId(row.externalId)?.country ?? null,
    imageUrl: join?.imageUrl ?? null,
    thumbUrl: join?.thumbUrl ?? join?.imageUrl ?? null,
    gallery: join?.gallery ?? [],
    productLevel: join?.productLevel ?? null,
    familyKey: join?.familyKey ?? null,
    failed,
    retryable: false,
    status: row.status,
    errorMessage: row.errorMessage,
    pre,
    post,
    delta: pre !== null && post !== null ? post - pre : null,
    tier: sample.post?.tier ?? sample.pre?.tier ?? null,
    fields,
    fieldScores,
    findings,
    reasons,
    changedWords,
    changeSize: changeSizeOf(changedWords),
    worst: worstFieldOf(fieldScores, findings),
    risk: riskOf(reasons, pre, post),
    row,
  };
}

/** One failed-products row → review item (no fields, no scores; always "Generation failed"). */
function failedItem(row: FailedProduct, join: ReviewJoin | undefined): ReviewItem {
  const findings: ReviewFinding[] = [
    { reason: 'generationFailed', field: null, detail: row.errorMessage },
  ];
  const reasons = reasonsOf(findings);
  return {
    productId: row.productId,
    externalId: row.externalId,
    title: join?.title ?? parseSourceProductId(row.externalId)?.offerId ?? row.externalId,
    brand: join?.brand ?? null,
    market: parseSourceProductId(row.externalId)?.country ?? null,
    imageUrl: join?.imageUrl ?? null,
    thumbUrl: join?.thumbUrl ?? join?.imageUrl ?? null,
    gallery: join?.gallery ?? [],
    productLevel: join?.productLevel ?? null,
    familyKey: join?.familyKey ?? null,
    failed: true,
    retryable: row.retryable,
    status: 'FAILED',
    errorMessage: row.errorMessage,
    pre: null,
    post: null,
    delta: null,
    tier: null,
    fields: [],
    fieldScores: {},
    findings,
    reasons,
    changedWords: 0,
    changeSize: 'none',
    worst: null,
    risk: riskOf(reasons, null, null),
    row: null,
  };
}

/**
 * Merges the sampled enriched rows and failed rows into one queue (one item per product — a
 * failed-products row wins the `failed` / `retryable` flags for a product listed in both).
 */
export function buildReviewItems(
  enriched: readonly EnrichedSampleRow[],
  failed: readonly FailedProduct[],
  joins: ReadonlyMap<string, ReviewJoin>,
  requested: ReadonlySet<string>,
): ReviewItem[] {
  const byId = new Map<string, ReviewItem>();
  for (const sample of enriched) {
    byId.set(
      sample.row.productId,
      enrichedItem(sample, joins.get(sample.row.productId), requested),
    );
  }
  for (const row of failed) {
    const existing = byId.get(row.productId);
    if (existing) {
      const findings = existing.findings.some((f) => f.reason === 'generationFailed')
        ? existing.findings
        : [
            { reason: 'generationFailed' as const, field: null, detail: row.errorMessage },
            ...existing.findings,
          ];
      const reasons = reasonsOf(findings);
      byId.set(row.productId, {
        ...existing,
        failed: true,
        retryable: row.retryable,
        errorMessage: existing.errorMessage ?? row.errorMessage,
        findings,
        reasons,
        risk: riskOf(reasons, existing.pre, existing.post),
      });
    } else {
      byId.set(row.productId, failedItem(row, joins.get(row.productId)));
    }
  }
  return applyVariantConsistency([...byId.values()]);
}

// ---------------------------------------------------------------------------------------------
// Variant consistency (conservative, cross-item heuristic)
// ---------------------------------------------------------------------------------------------

/** A variant's own size / colour attribute words — stripped before comparing siblings' generated
 *  text, so two correctly-written variants are never flagged just for naming their own size or
 *  colour. Short and conservative on purpose: a miss here only means a real inconsistency goes
 *  unflagged, never that a correct rewrite gets flagged. */
const VARIANT_ATTRIBUTE_WORDS = new Set([
  'black',
  'white',
  'grey',
  'gray',
  'red',
  'blue',
  'green',
  'yellow',
  'pink',
  'purple',
  'orange',
  'brown',
  'beige',
  'navy',
  'gold',
  'silver',
  'cream',
  'ivory',
  'tan',
  'multicolor',
  'multicolour',
  'small',
  'medium',
  'large',
  'xs',
  's',
  'm',
  'l',
  'xl',
  'xxl',
  'mini',
  'petite',
  'plus',
]);
/** Unit / number tokens (`numberUnitFacts`' vocabulary, as bare words) stripped the same way —
 *  "1 fl oz" vs "1.7 oz" must normalise to the same text if the WORDS around them agree. */
const VARIANT_UNIT_WORDS = new Set([
  'fl',
  'oz',
  'ml',
  'cl',
  'l',
  'mg',
  'kg',
  'g',
  'lbs',
  'lb',
  'cm',
  'mm',
  'm',
  'in',
  'inch',
  'inches',
  'pcs',
  'pack',
  'ct',
  'count',
  'hrs',
  'hour',
  'hours',
  'h',
  'x',
  'spf',
]);
/** Shortest normalised text worth comparing — below this, a plausible rewording looks identical
 *  to a real drop, so the heuristic stays silent rather than guess. */
const SIBLING_COMPARE_MIN_LENGTH = 12;

/** `text` stripped of its own size / colour / volume words and bare numbers, for comparing one
 *  variant's generated text against its siblings' without the words that are SUPPOSED to differ.
 *  Review fix — `SPF_RE` / `NUMBER_UNIT_RE` strip whole number+unit facts FIRST, space or no space
 *  ("1.7oz", "50ml") and decimals written with `.` or `,`: the word tokenizer never splits a digit
 *  from an adjacent letter, so a word-level filter alone left facts like "1.7oz" tokenized as `1`
 *  + `7oz` — neither a bare number nor a known unit word — surviving into the comparison. */
function normalizeForSiblingCompare(text: string | null): string {
  if (!text) return '';
  const stripped = text.replace(SPF_RE, ' ').replace(NUMBER_UNIT_RE, ' ');
  return words(stripped)
    .filter(
      (w) =>
        !VARIANT_ATTRIBUTE_WORDS.has(w) && !VARIANT_UNIT_WORDS.has(w) && !/^\d+([.,]\d+)?$/.test(w),
    )
    .join(' ');
}

/**
 * The new `variantInconsistent` reason: for each style family (`familyKey`) of two or more
 * VARIANTs in the sample, compares each requested field's generated text across the family once
 * the variant's own attribute words are stripped out. A variant is flagged on a field only when
 * EVERY other sibling's normalised text differs from its own (one sibling agreeing is enough to
 * stay silent) and both texts clear `SIBLING_COMPARE_MIN_LENGTH` — conservative by design: with
 * exactly two siblings that genuinely disagree, both sides get flagged (there is no basis here to
 * call one of them "wrong"); a 3+-member family flags only the true outlier(s) against the rest.
 * Returns the extra findings per product id; callers merge them in (`applyVariantConsistency`).
 */
export function variantConsistencyFindings(
  items: readonly ReviewItem[],
): Map<string, ReviewFinding[]> {
  const extra = new Map<string, ReviewFinding[]>();
  const byFamily = new Map<string, ReviewItem[]>();
  for (const item of items) {
    if (item.productLevel !== 'VARIANT' || !item.familyKey) continue;
    const bucket = byFamily.get(item.familyKey) ?? [];
    bucket.push(item);
    byFamily.set(item.familyKey, bucket);
  }
  for (const siblings of byFamily.values()) {
    if (siblings.length < 2) continue;
    const fieldKeys = new Set(siblings.flatMap((s) => s.fields.map((f) => fieldKey(f.name))));
    for (const key of fieldKeys) {
      const texts = siblings.map((s) => {
        const field = s.fields.find((f) => fieldKey(f.name) === key);
        return field && isGenerated(field) ? normalizeForSiblingCompare(afterText(field)) : null;
      });
      siblings.forEach((item, i) => {
        const mine = texts[i];
        if (!mine || mine.length < SIBLING_COMPARE_MIN_LENGTH) return;
        const others = texts.filter((t, j): t is string => j !== i && t !== null);
        if (others.length === 0 || others.some((t) => t === mine)) return;
        const bucket = extra.get(item.productId) ?? [];
        bucket.push({ reason: 'variantInconsistent', field: key, detail: null });
        extra.set(item.productId, bucket);
      });
    }
  }
  return extra;
}

/** Merges `variantConsistencyFindings` into each item's findings / reasons / risk — a pure pass
 *  over the already-built items, so the cross-item heuristic never complicates `enrichedItem`. */
export function applyVariantConsistency(items: readonly ReviewItem[]): ReviewItem[] {
  const extra = variantConsistencyFindings(items);
  if (extra.size === 0) return [...items];
  return items.map((item) => {
    const add = extra.get(item.productId);
    if (!add || add.length === 0) return item;
    const findings = [...item.findings, ...add];
    const reasons = reasonsOf(findings);
    return {
      ...item,
      findings,
      reasons,
      worst: item.worst ?? worstFieldOf(item.fieldScores, findings),
      risk: riskOf(reasons, item.pre, item.post),
    };
  });
}

// ---------------------------------------------------------------------------------------------
// Product level + family grouping
// ---------------------------------------------------------------------------------------------

/** `ReviewFilters.productLevel` sentinel for "no matched product" (`item.productLevel === null`) —
 *  a plain string so a Radix `Select` (which cannot hold `null` as an item value) can carry it. */
export const UNMATCHED_LEVEL = '__unmatched';

/** One Type-filter option: a `productLevel` value (or `null` for "unmatched") and its count. */
export interface LevelCount {
  level: string | null;
  count: number;
}

/** Distinct `productLevel` values present in the sample, each with its count — the Type filter's
 *  options ("Unique 8 · Variant 10 · Set 2"), largest first. */
export function productLevelCounts(items: readonly ReviewItem[]): LevelCount[] {
  const counts = new Map<string | null, number>();
  for (const item of items) counts.set(item.productLevel, (counts.get(item.productLevel) ?? 0) + 1);
  return [...counts.entries()]
    .map(([level, count]) => ({ level, count }))
    .sort((a, b) => b.count - a.count || (a.level ?? '').localeCompare(b.level ?? ''));
}

/** "Group: none | Family" — the queue / focus list's layout, never a filter: it reorders and
 *  clusters what is already shown, it never hides a product. */
export type ReviewGroupBy = 'none' | 'family';
export const REVIEW_GROUP_BYS: readonly ReviewGroupBy[] = ['none', 'family'];

/** One style family clustered for "Group: Family": its members and how many disagree. */
export interface FamilyGroup {
  familyKey: string;
  /** Stand-in family name — the curation join carries no family title, so this is the shortest
   *  member's title (usually the one with the fewest size/colour words tacked on). */
  title: string;
  items: ReviewItem[];
  inconsistentCount: number;
}

/**
 * Buckets items sharing a non-null `familyKey` into family groups — two or more members only; a
 * product whose family has no OTHER member in the current sample has nothing to be grouped with
 * and stays a single. Groups sort by their inconsistency count, then size, largest first.
 */
export function familyGroups(items: readonly ReviewItem[]): {
  groups: FamilyGroup[];
  singles: ReviewItem[];
} {
  const byKey = new Map<string, ReviewItem[]>();
  const singles: ReviewItem[] = [];
  for (const item of items) {
    if (!item.familyKey) {
      singles.push(item);
      continue;
    }
    const bucket = byKey.get(item.familyKey) ?? [];
    bucket.push(item);
    byKey.set(item.familyKey, bucket);
  }
  const groups: FamilyGroup[] = [];
  for (const [familyKey, bucket] of byKey) {
    if (bucket.length < 2) {
      singles.push(...bucket);
      continue;
    }
    const title =
      [...bucket].sort((a, b) => a.title.length - b.title.length)[0]?.title ?? familyKey;
    const inconsistentCount = bucket.filter((i) =>
      i.reasons.includes('variantInconsistent'),
    ).length;
    groups.push({ familyKey, title, items: bucket, inconsistentCount });
  }
  groups.sort(
    (a, b) => b.inconsistentCount - a.inconsistentCount || b.items.length - a.items.length,
  );
  return { groups, singles };
}

// ---------------------------------------------------------------------------------------------
// Level 1 aggregates (over the sample)
// ---------------------------------------------------------------------------------------------

/** One field chip's numbers (the chip shows the flag count; the rest goes in its tooltip). */
export interface FieldAggregate {
  key: string;
  /** Products in the sample that list this field. */
  listed: number;
  /** Of those, how many got a generated value. */
  generated: number;
  /** Mean changed words where generated (word-set estimate), null when nothing was generated. */
  avgChangedWords: number | null;
  /** Products with a finding pinned to this field. */
  needsLook: number;
}

/** Per requested field: generated / avg change / needs-a-look over the sampled items. */
export function fieldAggregates(
  items: readonly ReviewItem[],
  keys: readonly string[],
): FieldAggregate[] {
  return keys.map((key) => {
    let listed = 0;
    let generated = 0;
    let changed = 0;
    let needsLook = 0;
    for (const item of items) {
      const field = item.fields.find((f) => fieldKey(f.name) === key);
      if (field) {
        listed += 1;
        if (isGenerated(field)) {
          generated += 1;
          const { removed, added } = wordBagDiff(fieldValueText(field.before), afterText(field));
          changed += Math.max(removed.length, added.length);
        }
      }
      if (item.findings.some((f) => f.field === key)) needsLook += 1;
    }
    return {
      key,
      listed,
      generated,
      avgChangedWords: generated > 0 ? changed / generated : null,
      needsLook,
    };
  });
}

/** Reasons that concern the whole product, so their "needs your eye" line is never per field. */
const PRODUCT_REASONS: ReadonlySet<ReviewReason> = new Set(['generationFailed', 'scoreDropped']);

/** Reasons whose finding detail is a value worth quoting as an example ("150 ml", "organic"). */
export const EXAMPLE_REASONS: ReadonlySet<ReviewReason> = new Set([
  'numbersRemoved',
  'claimAdded',
  'brandDropped',
]);

/** One "What needs your eye" line: a reason on one field (or the whole product) and its count. */
export interface ReasonGroup {
  reason: ReviewReason;
  /** The field the reason hit, null for product-level reasons (failed, score dropped). */
  field: string | null;
  /** Distinct products in the sample with this reason on this field. */
  count: number;
  /** One quoted value from the evidence ("150 ml"), when the reason has one. */
  example: string | null;
}

/**
 * The sample's findings grouped by reason × field — one plain-language line each on Level 1 —
 * sorted by product count, ties broken by severity (REVIEW_REASONS order) then field order.
 */
export function reasonGroups(
  items: readonly ReviewItem[],
  fieldOrder: readonly string[],
): ReasonGroup[] {
  const groups = new Map<string, { group: ReasonGroup; ids: Set<string> }>();
  for (const item of items) {
    for (const finding of item.findings) {
      const field = PRODUCT_REASONS.has(finding.reason) ? null : finding.field;
      const id = `${finding.reason}|${field ?? ''}`;
      const entry = groups.get(id) ?? {
        group: { reason: finding.reason, field, count: 0, example: null },
        ids: new Set<string>(),
      };
      entry.ids.add(item.productId);
      entry.group.count = entry.ids.size;
      if (entry.group.example === null && EXAMPLE_REASONS.has(finding.reason) && finding.detail) {
        entry.group.example = finding.detail.split(', ')[0] ?? null;
      }
      groups.set(id, entry);
    }
  }
  const fieldRank = (f: string | null) => (f === null ? -1 : fieldOrder.indexOf(f));
  return [...groups.values()]
    .map((entry) => entry.group)
    .sort(
      (a, b) =>
        b.count - a.count ||
        REVIEW_REASONS.indexOf(a.reason) - REVIEW_REASONS.indexOf(b.reason) ||
        fieldRank(a.field) - fieldRank(b.field),
    );
}

/** FNV-1a 32-bit hash — a cheap, stable pseudo-random rank for seeded sampling. */
function hash32(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * A seeded random sample of `size` products: each id is ranked by hash(seed + id) and the lowest
 * ranks win, so the same seed always picks the same products (stable spot-check for a session).
 */
export function seededSample(ids: readonly string[], size: number, seed: number): string[] {
  return [...ids]
    .map((id) => ({ id, rank: hash32(`${String(seed)}:${id}`) }))
    .sort((a, b) => a.rank - b.rank || a.id.localeCompare(b.id))
    .slice(0, size)
    .map((x) => x.id);
}

// ---------------------------------------------------------------------------------------------
// Level 2 filtering + sorting
// ---------------------------------------------------------------------------------------------

/** Score bands for the Score ▾ filter; `below` uses the scorecard's threshold (70 by default). */
export type ScoreBand = 'below' | 'fair' | 'good' | 'top' | 'unscored';
export const SCORE_BANDS: readonly ScoreBand[] = ['below', 'fair', 'good', 'top', 'unscored'];
export const CHANGE_SIZES: readonly ChangeSize[] = ['none', 'small', 'medium', 'large'];

export type ReviewSort = 'risk' | 'drop' | 'lowest' | 'change' | 'name';
export const REVIEW_SORTS: readonly ReviewSort[] = ['risk', 'drop', 'lowest', 'change', 'name'];

/** Queue filters; `null` = any. `reason: 'any'` = every product with at least one reason. */
export interface ReviewFilters {
  search: string;
  field: string | null;
  score: ScoreBand | null;
  change: ChangeSize | null;
  reason: ReviewReason | 'any' | null;
  brand: string | null;
  market: string | null;
  /** The SERVER's PASS/REVIEW/FAIL tier (RESULT bento's FAIL tile) — distinct from `score`, which
   *  bands the client's own number against the run's threshold. */
  tier: 'PASS' | 'REVIEW' | 'FAIL' | 'NEEDS_LOOK' | null;
  /** One `productLevel` value (the Type filter), `UNMATCHED_LEVEL` for the null bucket, or null = any. */
  productLevel: string | null;
  /** An explicit product set (the "Spot-check" sample); null = no id filter. */
  ids: readonly string[] | null;
  sort: ReviewSort;
}

export const EMPTY_FILTERS: ReviewFilters = {
  search: '',
  field: null,
  score: null,
  change: null,
  reason: null,
  brand: null,
  market: null,
  tier: null,
  productLevel: null,
  ids: null,
  sort: 'risk',
};

/** The band one score falls in, given the "below" threshold. */
export function scoreBandOf(value: number | null, threshold: number): ScoreBand {
  if (value === null) return 'unscored';
  if (value < threshold) return 'below';
  if (value < SCORE_BAND.fair) return 'fair';
  if (value < SCORE_BAND.good) return 'good';
  return 'top';
}

/** True when an item passes every set filter. */
function matches(item: ReviewItem, f: ReviewFilters, threshold: number): boolean {
  if (f.ids && !f.ids.includes(item.productId)) return false;
  if (f.reason === 'any' && item.reasons.length === 0) return false;
  if (f.reason && f.reason !== 'any' && !item.reasons.includes(f.reason)) return false;
  // Reason + field together (a "needs your eye" line) must hit the SAME finding.
  const specific = f.reason && f.reason !== 'any' && !PRODUCT_REASONS.has(f.reason);
  if (
    f.field &&
    !item.findings.some((x) => x.field === f.field && (!specific || x.reason === f.reason))
  ) {
    return false;
  }
  if (f.score && scoreBandOf(item.post ?? item.pre, threshold) !== f.score) return false;
  if (f.tier === 'NEEDS_LOOK') {
    // The lens segment's rule — the same REVIEW + FAIL the verdict and its count lead with.
    if (item.tier !== 'REVIEW' && item.tier !== 'FAIL') return false;
  } else if (f.tier && item.tier !== f.tier) return false;
  if (f.productLevel) {
    const matchesLevel =
      f.productLevel === UNMATCHED_LEVEL
        ? item.productLevel === null
        : item.productLevel === f.productLevel;
    if (!matchesLevel) return false;
  }
  if (f.change && item.changeSize !== f.change) return false;
  if (f.brand && item.brand !== f.brand) return false;
  if (f.market && item.market !== f.market) return false;
  const needle = f.search.trim().toLowerCase();
  if (needle) {
    const offer = parseSourceProductId(item.externalId)?.offerId ?? '';
    return (
      item.title.toLowerCase().includes(needle) ||
      offer.toLowerCase().includes(needle) ||
      item.productId.toLowerCase().startsWith(needle)
    );
  }
  return true;
}

/** Sort comparator per sort key; ties fall back to the title so the order is stable. */
function compare(sort: ReviewSort): (a: ReviewItem, b: ReviewItem) => number {
  const byTitle = (a: ReviewItem, b: ReviewItem) => a.title.localeCompare(b.title);
  switch (sort) {
    case 'drop':
      return (a, b) => (a.delta ?? 0) - (b.delta ?? 0) || byTitle(a, b);
    case 'lowest':
      return (a, b) => (a.post ?? a.pre ?? 101) - (b.post ?? b.pre ?? 101) || byTitle(a, b);
    case 'change':
      return (a, b) => b.changedWords - a.changedWords || byTitle(a, b);
    case 'name':
      return byTitle;
    default:
      return (a, b) => b.risk - a.risk || byTitle(a, b);
  }
}

/** The queue: filtered then sorted (risk-first by default). */
export function filterAndSort(
  items: readonly ReviewItem[],
  filters: ReviewFilters,
  threshold: number,
): ReviewItem[] {
  return items.filter((i) => matches(i, filters, threshold)).sort(compare(filters.sort));
}

/** Distinct non-empty values of one item property, sorted — options for Brand ▾ / Market ▾. */
export function distinctValues(
  items: readonly ReviewItem[],
  pick: (item: ReviewItem) => string | null,
): string[] {
  return [...new Set(items.map(pick).filter((v): v is string => !!v))].sort((a, b) =>
    a.localeCompare(b),
  );
}

/** The item-retry stage the review regenerates with: ENRICHMENT when offered, else the gate's first. */
export function reviewRetryStage(pendingGate: PendingGate | null): string {
  const stages = pendingGate?.itemRetryStages ?? [];
  return stages.includes('ENRICHMENT') ? 'ENRICHMENT' : (stages[0] ?? '');
}
